import React, { useState, useEffect, useRef } from 'react';
import { AdminService, ProgramConfig } from '../../services/adminService';
import { isDevelopment } from '../../config/constants';
import PanelEditor from './PanelEditor';
import { useDialog } from '../../context/DialogContext';
import styles from '../../styles/admin/programsAdmin.module.css';

interface ProgramsAdminProps {
  user: any;
}

const ProgramsAdmin: React.FC<ProgramsAdminProps> = ({ user }) => {
  const { showAlert, showConfirm } = useDialog();
  const showError = (message: string) => showAlert({ title: 'Something went wrong', message, okText: 'OK', variant: 'danger' });
  const [selectedProgram, setSelectedProgram] = useState<string>('saa');
  const [config, setConfig] = useState<ProgramConfig | null>(null);
  const [originalConfig, setOriginalConfig] = useState<ProgramConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [changeSummary, setChangeSummary] = useState<string>('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoFileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingTabIcon, setUploadingTabIcon] = useState<{ [key: string]: boolean }>({});
  const tabIconFileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  
  // Expanded sections state
  const [expandedGeneral, setExpandedGeneral] = useState(false);
  const [expandedTab2, setExpandedTab2] = useState(false);
  const [expandedTab3, setExpandedTab3] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set());

  const availablePrograms = getAvailablePrograms(user?.admin_rights || []);

  useEffect(() => {
    if (availablePrograms.length > 0) {
      setSelectedProgram(availablePrograms[0]);
    }
  }, []);

  useEffect(() => {
    if (selectedProgram) {
      loadConfig();
    }
  }, [selectedProgram]);

  useEffect(() => {
    // Detect unsaved changes
    if (config && originalConfig) {
      setHasUnsavedChanges(JSON.stringify(config) !== JSON.stringify(originalConfig));
    }
  }, [config, originalConfig]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const programConfig = await AdminService.getProgramConfigAdmin(selectedProgram);
      setConfig(programConfig);
      setOriginalConfig(JSON.parse(JSON.stringify(programConfig))); // Deep clone
    } catch (err: any) {
      console.error('Error loading program config:', err);
      setError(err.message || 'Failed to load program configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const historyData = await AdminService.getProgramHistory(selectedProgram, 50);
      setHistory(historyData);
      setShowHistory(true);
    } catch (err) {
      console.error('Error loading history:', err);
    }
  };

  const handleSaveClick = () => {
    if (!config || !originalConfig) return;

    // Generate change summary
    const summary = generateChangeSummary(originalConfig, config);
    setChangeSummary(summary);
    setShowSaveConfirmation(true);
  };

  const handleSaveConfirm = async () => {
    if (!config) return;

    try {
      setSaving(true);
      setError(null);
      
      const articlesPanel = config.tab2.panels.find((p: any) => p.id === 'articles' || p.id === 'articles-panel');
      if (isDevelopment() && articlesPanel && articlesPanel.items) {
        console.log('Before save - Articles panel items:', articlesPanel.items.length);
        articlesPanel.items.forEach((item: any, idx: number) => {
          if (item.link_type === 'document') {
            console.log(`Before save - Item ${idx} (${item.title?.substring(0, 30)}): link_type=${item.link_type}, link_value=${item.link_value}`);
          }
        });
      }
      
      const articlesPanelToSave = config.tab2.panels.find((p: any) => p.id === 'articles' || p.id === 'articles-panel');
      if (isDevelopment() && articlesPanelToSave && articlesPanelToSave.items) {
        console.log('Saving config with document items:');
        articlesPanelToSave.items.forEach((item: any, idx: number) => {
          if (item.link_type === 'document') {
            console.log(`  Item ${idx}: link_value =`, item.link_value, '(type:', typeof item.link_value, ')');
          }
        });
      }
      
      await AdminService.updateProgramConfig(selectedProgram, config);
      
      // Reload config to get updated metadata
      await loadConfig();
      
      const reloadedConfig = await AdminService.getProgramConfigAdmin(selectedProgram);
      const reloadedArticlesPanel = reloadedConfig.tab2.panels.find((p: any) => p.id === 'articles' || p.id === 'articles-panel');
      if (isDevelopment() && reloadedArticlesPanel && reloadedArticlesPanel.items) {
        console.log('After reload - Articles panel items:', reloadedArticlesPanel.items.length);
        reloadedArticlesPanel.items.forEach((item: any, idx: number) => {
          if (item.link_type === 'document') {
            console.log(`After reload - Item ${idx} (${item.title?.substring(0, 30)}): link_type=${item.link_type}, link_value=${item.link_value}`);
          }
        });
      }
      
      setHasUnsavedChanges(false);
      setShowSaveConfirmation(false);
      
      showAlert({
        title: 'Changes published',
        message: 'The live site will update within 60 seconds.',
        okText: 'OK',
        variant: 'success',
      });
    } catch (err: any) {
      console.error('Error saving config:', err);
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (
    updater: ProgramConfig | ((prevConfig: ProgramConfig) => ProgramConfig)
  ) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const nextConfig =
        typeof updater === 'function'
          ? (updater as (prevConfig: ProgramConfig) => ProgramConfig)(prevConfig)
          : updater;
      return nextConfig;
    });
  };

  const handleLogoUpload = async (file: File) => {
    try {
      setUploadingLogo(true);
      // Upload logo to programs/{program_id}/icons/ directory
      const response = await AdminService.uploadProgramLogo(file, selectedProgram);
      if (isDevelopment()) console.log('Logo upload response:', response);
      
      if (!response || !response.url) {
        throw new Error('Invalid response from upload endpoint');
      }
      
      // URL is already in path-style format from backend
      const logoUrl = response.url;
      
      // Update config with new logo URL
      handleConfigChange((prevConfig) => ({
        ...prevConfig,
        logo_url: logoUrl
      }));
      
      // Reset file input
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error uploading logo:', err);
      showError(`Failed to upload logo: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleTabIconUpload = async (tabKey: 'tab1' | 'tab2' | 'tab3', file: File) => {
    try {
      setUploadingTabIcon({ ...uploadingTabIcon, [tabKey]: true });
      // Upload icon to programs/{program_id}/icons/ directory (same as panel icons)
      const response = await AdminService.uploadProgramImage(file, selectedProgram);
      if (isDevelopment()) console.log('Tab icon upload response:', response);
      
      if (!response || !response.url) {
        throw new Error('Invalid response from upload endpoint');
      }
      
      // Update config with new icon URL
      handleConfigChange((prevConfig) => {
        const updatedConfig = { ...prevConfig };
        if (tabKey === 'tab1') {
          updatedConfig.tab1 = { ...(updatedConfig.tab1 || {}), icon_url: response.url };
        } else if (tabKey === 'tab2') {
          updatedConfig.tab2 = { ...updatedConfig.tab2, icon_url: response.url };
        } else if (tabKey === 'tab3') {
          updatedConfig.tab3 = { ...(updatedConfig.tab3 || {}), icon_url: response.url };
        }
        return updatedConfig;
      });
      
      // Reset file input
      if (tabIconFileInputRefs.current[tabKey]) {
        tabIconFileInputRefs.current[tabKey]!.value = '';
      }
    } catch (err) {
      console.error('Error uploading tab icon:', err);
      showError(`Failed to upload tab icon: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUploadingTabIcon({ ...uploadingTabIcon, [tabKey]: false });
    }
  };

  const togglePanel = (panelId: string) => {
    const newExpanded = new Set(expandedPanels);
    if (newExpanded.has(panelId)) {
      newExpanded.delete(panelId);
    } else {
      newExpanded.add(panelId);
    }
    setExpandedPanels(newExpanded);
  };

  const movePanelOrder = (panelId: string, direction: 'up' | 'down' | 'top' | 'bottom') => {
    handleConfigChange((prevConfig) => {
      const panels = [...prevConfig.tab2.panels].sort((a, b) => a.order - b.order);
      const currentIndex = panels.findIndex((p) => p.id === panelId);

      let newIndex = currentIndex;
      if (direction === 'up' && currentIndex > 0) {
        newIndex = currentIndex - 1;
      } else if (direction === 'down' && currentIndex < panels.length - 1) {
        newIndex = currentIndex + 1;
      } else if (direction === 'top') {
        newIndex = 0;
      } else if (direction === 'bottom') {
        newIndex = panels.length - 1;
      }

      if (newIndex === currentIndex) {
        return prevConfig;
      }

      const updatedPanels = [...panels];
      const [removed] = updatedPanels.splice(currentIndex, 1);
      updatedPanels.splice(newIndex, 0, removed);

      const reorderedPanels = updatedPanels.map((p, i) => ({ ...p, order: i + 1 }));

      return {
        ...prevConfig,
        tab2: {
          ...prevConfig.tab2,
          panels: reorderedPanels,
        },
      };
    });
  };

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner}></div>
        <p>Loading program configuration...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <p>{error}</p>
        <button onClick={loadConfig} className={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  if (!config) {
    return (
      <div className={styles.emptyState}>
        <p>No configuration found for this program</p>
      </div>
    );
  }

  return (
    <div className={styles.programsAdmin}>
      {/* Program Selector */}
      {availablePrograms.length > 1 && (
        <div className={styles.programSelector}>
          <label htmlFor="program-select">Program:</label>
          <select
            id="program-select"
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            className={styles.select}
            disabled={hasUnsavedChanges}
          >
            {availablePrograms.map(program => (
              <option key={program} value={program}>
                {program.toUpperCase()}
              </option>
            ))}
          </select>
          {hasUnsavedChanges && (
            <span className={styles.unsavedWarning}>
              (Save or discard changes before switching programs)
            </span>
          )}
        </div>
      )}

      {/* Header with Actions */}
      <div className={styles.header}>
        <div>
          <h2>Managing: {selectedProgram.toUpperCase()} Homepage</h2>
          <p className={styles.lastUpdated}>
            Last updated: {new Date(config.updated_at).toLocaleString()} by {config.updated_by}
          </p>
        </div>
        <div className={styles.headerActions}>
          <button onClick={loadHistory} className={styles.historyButton}>
            📜 View History
          </button>
          {hasUnsavedChanges && (
            <button onClick={loadConfig} className={styles.discardButton}>
              Discard Changes
            </button>
          )}
          <button
            onClick={handleSaveClick}
            className={styles.saveButton}
            disabled={!hasUnsavedChanges || saving}
          >
            {saving ? 'Publishing...' : 'Save & Publish'}
          </button>
        </div>
      </div>

      {hasUnsavedChanges && (
        <div className={styles.unsavedBanner}>
          ⚠️ You have unsaved changes
        </div>
      )}

      {/* Configuration Editor - Accordion Layout */}
      <div className={styles.editorContainer}>
        
        {/* General Settings Panel */}
        <div className={styles.accordionPanel}>
          <div className={styles.accordionHeader} onClick={() => setExpandedGeneral(!expandedGeneral)}>
            <h3>General Settings</h3>
            <button className={`${styles.expandIcon} ${expandedGeneral ? styles.expanded : ''}`}>
              ▼
            </button>
          </div>
          {expandedGeneral && (
            <div className={styles.accordionContent}>
              <div className={styles.formGroup}>
                <label>Page Title</label>
                <input
                  type="text"
                  value={config.title}
                  onChange={(e) =>
                    handleConfigChange((prevConfig) => ({ ...prevConfig, title: e.target.value }))
                  }
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Subtitle / About Text</label>
                <textarea
                  value={config.subtitle}
                  onChange={(e) =>
                    handleConfigChange((prevConfig) => ({ ...prevConfig, subtitle: e.target.value }))
                  }
                  className={styles.textarea}
                  rows={4}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Program Logo</label>
                <div className={styles.logoUploadGroup}>
                  {config.logo_url && (
                    <div className={styles.logoPreview}>
                      <img 
                        src={config.logo_url} 
                        alt="Program logo" 
                        className={styles.logoPreviewImage}
                        onError={(e) => {
                          // If image fails to load, show placeholder
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <span className={styles.logoUrl}>{config.logo_url}</span>
                    </div>
                  )}
                  <input
                    ref={logoFileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleLogoUpload(e.target.files[0]);
                      }
                    }}
                    disabled={uploadingLogo}
                    className={styles.fileInput}
                  />
                  {uploadingLogo && <span className={styles.uploading}>Uploading...</span>}
                  {!config.logo_url && (
                    <p className={styles.helpText}>
                      Upload a logo image to display at the top of the program page. Recommended size: 120x120px.
                    </p>
                  )}
                </div>
              </div>

              {/* Tab Configuration */}
              <div className={styles.sectionDivider}></div>
              <h4 className={styles.sectionTitle}>Tab Configuration</h4>
              <p className={styles.helpText} style={{ marginBottom: '20px' }}>
                Configure icons and accent colors for the three main tabs on the homepage.
              </p>

              {/* Tab 1 (Take Action / Work My Steps) */}
              <div className={styles.tabConfigSection}>
                <h5 className={styles.tabConfigTitle}>Tab 1: Work My Steps</h5>
                <div className={styles.formGroup}>
                  <label>Tab Icon (optional)</label>
                  <div className={styles.logoUploadGroup}>
                    {config.tab1?.icon_url && (
                      <div className={styles.logoPreview}>
                        <img 
                          src={config.tab1.icon_url} 
                          alt="Tab 1 icon" 
                          className={styles.logoPreviewImage}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <span className={styles.logoUrl}>{config.tab1.icon_url}</span>
                        <button
                          onClick={() => handleConfigChange((prevConfig) => ({
                            ...prevConfig,
                            tab1: { ...(prevConfig.tab1 || {}), icon_url: undefined }
                          }))}
                          className={styles.removeImageButton}
                          style={{ marginTop: '8px' }}
                        >
                          Remove Icon
                        </button>
                      </div>
                    )}
                    <input
                      ref={(el) => { tabIconFileInputRefs.current['tab1'] = el; }}
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleTabIconUpload('tab1', e.target.files[0]);
                        }
                      }}
                      disabled={uploadingTabIcon['tab1']}
                      className={styles.fileInput}
                    />
                    {uploadingTabIcon['tab1'] && <span className={styles.uploading}>Uploading...</span>}
                    {!config.tab1?.icon_url && (
                      <p className={styles.helpText}>
                        Upload an icon image for this tab. Recommended size: 32-40px.
                      </p>
                    )}
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Accent Color (optional)</label>
                  <select
                    value={config.tab1?.accent_color || ''}
                    onChange={(e) =>
                      handleConfigChange((prevConfig) => ({
                        ...prevConfig,
                        tab1: { ...prevConfig.tab1, accent_color: e.target.value || undefined }
                      }))
                    }
                    className={styles.select}
                  >
                    <option value="">Default (Green)</option>
                    <option value="#4CAF50">Green</option>
                    <option value="#2196F3">Blue</option>
                    <option value="#9C27B0">Purple</option>
                    <option value="#FF9800">Orange</option>
                    <option value="#F44336">Red</option>
                    <option value="#00BCD4">Cyan</option>
                    <option value="#8BC34A">Light Green</option>
                    <option value="#FF5722">Deep Orange</option>
                  </select>
                  <p className={styles.helpText}>Choose a color for the tab border and active state</p>
                </div>
              </div>

              {/* Tab 2 (Resources) */}
              <div className={styles.tabConfigSection}>
                <h5 className={styles.tabConfigTitle}>Tab 2: {config.tab2?.name || 'Resources & Reading'}</h5>
                <div className={styles.formGroup}>
                  <label>Tab Icon (optional)</label>
                  <div className={styles.logoUploadGroup}>
                    {config.tab2?.icon_url && (
                      <div className={styles.logoPreview}>
                        <img 
                          src={config.tab2.icon_url} 
                          alt="Tab 2 icon" 
                          className={styles.logoPreviewImage}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <span className={styles.logoUrl}>{config.tab2.icon_url}</span>
                        <button
                          onClick={() => handleConfigChange((prevConfig) => ({
                            ...prevConfig,
                            tab2: { ...prevConfig.tab2, icon_url: undefined }
                          }))}
                          className={styles.removeImageButton}
                          style={{ marginTop: '8px' }}
                        >
                          Remove Icon
                        </button>
                      </div>
                    )}
                    <input
                      ref={(el) => { tabIconFileInputRefs.current['tab2'] = el; }}
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleTabIconUpload('tab2', e.target.files[0]);
                        }
                      }}
                      disabled={uploadingTabIcon['tab2']}
                      className={styles.fileInput}
                    />
                    {uploadingTabIcon['tab2'] && <span className={styles.uploading}>Uploading...</span>}
                    {!config.tab2?.icon_url && (
                      <p className={styles.helpText}>
                        Upload an icon image for this tab. Recommended size: 32-40px.
                      </p>
                    )}
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Accent Color (optional)</label>
                  <select
                    value={config.tab2?.accent_color || ''}
                    onChange={(e) =>
                      handleConfigChange((prevConfig) => ({
                        ...prevConfig,
                        tab2: { ...prevConfig.tab2, accent_color: e.target.value || undefined }
                      }))
                    }
                    className={styles.select}
                  >
                    <option value="">Default (Green)</option>
                    <option value="#4CAF50">Green</option>
                    <option value="#2196F3">Blue</option>
                    <option value="#9C27B0">Purple</option>
                    <option value="#FF9800">Orange</option>
                    <option value="#F44336">Red</option>
                    <option value="#00BCD4">Cyan</option>
                    <option value="#8BC34A">Light Green</option>
                    <option value="#FF5722">Deep Orange</option>
                  </select>
                  <p className={styles.helpText}>Choose a color for the tab border and active state</p>
                </div>
              </div>

              {/* Tab 3 (Meetings) */}
              <div className={styles.tabConfigSection}>
                <h5 className={styles.tabConfigTitle}>Tab 3: {config.tab3?.name || 'Meetings & Workshops'}</h5>
                <div className={styles.formGroup}>
                  <label>Tab Icon (optional)</label>
                  <div className={styles.logoUploadGroup}>
                    {config.tab3?.icon_url && (
                      <div className={styles.logoPreview}>
                        <img 
                          src={config.tab3.icon_url} 
                          alt="Tab 3 icon" 
                          className={styles.logoPreviewImage}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <span className={styles.logoUrl}>{config.tab3.icon_url}</span>
                        <button
                          onClick={() => handleConfigChange((prevConfig) => ({
                            ...prevConfig,
                            tab3: { ...(prevConfig.tab3 || {}), icon_url: undefined }
                          }))}
                          className={styles.removeImageButton}
                          style={{ marginTop: '8px' }}
                        >
                          Remove Icon
                        </button>
                      </div>
                    )}
                    <input
                      ref={(el) => { tabIconFileInputRefs.current['tab3'] = el; }}
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleTabIconUpload('tab3', e.target.files[0]);
                        }
                      }}
                      disabled={uploadingTabIcon['tab3']}
                      className={styles.fileInput}
                    />
                    {uploadingTabIcon['tab3'] && <span className={styles.uploading}>Uploading...</span>}
                    {!config.tab3?.icon_url && (
                      <p className={styles.helpText}>
                        Upload an icon image for this tab. Recommended size: 32-40px.
                      </p>
                    )}
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Accent Color (optional)</label>
                  <select
                    value={config.tab3?.accent_color || ''}
                    onChange={(e) =>
                      handleConfigChange((prevConfig) => ({
                        ...prevConfig,
                        tab3: { ...(prevConfig.tab3 || {}), accent_color: e.target.value || undefined }
                      }))
                    }
                    className={styles.select}
                  >
                    <option value="">Default (Green)</option>
                    <option value="#4CAF50">Green</option>
                    <option value="#2196F3">Blue</option>
                    <option value="#9C27B0">Purple</option>
                    <option value="#FF9800">Orange</option>
                    <option value="#F44336">Red</option>
                    <option value="#00BCD4">Cyan</option>
                    <option value="#8BC34A">Light Green</option>
                    <option value="#FF5722">Deep Orange</option>
                  </select>
                  <p className={styles.helpText}>Choose a color for the tab border and active state</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tab 2 Settings Panel */}
        <div className={styles.accordionPanel}>
          <div className={styles.accordionHeader} onClick={() => setExpandedTab2(!expandedTab2)}>
            <h3>Resource Tab: {config.tab2.name}</h3>
            <button className={`${styles.expandIcon} ${expandedTab2 ? styles.expanded : ''}`}>
              ▼
            </button>
          </div>
          {expandedTab2 && (
            <div className={styles.accordionContent}>
              <div className={styles.formGroup}>
                <label>Tab Name</label>
                <input
                  type="text"
                  value={config.tab2.name}
                  onChange={(e) =>
                    handleConfigChange((prevConfig) => ({
                      ...prevConfig,
                      tab2: { ...prevConfig.tab2, name: e.target.value },
                    }))
                  }
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Header Text</label>
                <textarea
                  value={config.tab2.header_text}
                  onChange={(e) =>
                    handleConfigChange((prevConfig) => ({
                      ...prevConfig,
                      tab2: { ...prevConfig.tab2, header_text: e.target.value },
                    }))
                  }
                  className={styles.textarea}
                  rows={2}
                />
              </div>

              {/* Content Panels List */}
              <div className={styles.panelsSection}>
                <div className={styles.panelsHeader}>
                  <h4>Content Panels</h4>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      const newPanelId = `panel-${Date.now()}`;
                      handleConfigChange((prevConfig) => {
                        const newPanel = {
                          id: newPanelId,
                          title: 'New Panel',
                          subtext: '',
                          format: 'list_links' as const,
                          order: prevConfig.tab2.panels.length + 1,
                          items: [],
                        };
                        return {
                          ...prevConfig,
                          tab2: {
                            ...prevConfig.tab2,
                            panels: [...prevConfig.tab2.panels, newPanel],
                          },
                        };
                      });
                      togglePanel(newPanelId);
                    }} 
                    className={styles.addPanelButton}
                    disabled={config.tab2.panels.length >= 9}
                  >
                    + Add Panel
                  </button>
                </div>

                {/* Content Panels - Each with expand/contract */}
                {[...config.tab2.panels].sort((a, b) => a.order - b.order).map((panel, index) => (
                  <div key={panel.id} className={styles.contentPanel}>
                    <div className={styles.contentPanelHeader} onClick={() => togglePanel(panel.id)}>
                      <div className={styles.panelInfo}>
                        <strong>{panel.title}</strong>
                        <span className={styles.panelMeta}>
                          ({panel.format.replace(/_/g, ' ')}) • {panel.items.length} items
                        </span>
                      </div>
                      <div className={styles.panelActions} onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => movePanelOrder(panel.id, 'top')}
                          disabled={index === 0}
                          className={styles.moveButton}
                          title="Move to top"
                        >
                          ⏫
                        </button>
                        <button
                          onClick={() => movePanelOrder(panel.id, 'up')}
                          disabled={index === 0}
                          className={styles.moveButton}
                          title="Move up one"
                        >
                          ⬆️
                        </button>
                        <button
                          onClick={() => movePanelOrder(panel.id, 'down')}
                          disabled={index === config.tab2.panels.length - 1}
                          className={styles.moveButton}
                          title="Move down one"
                        >
                          ⬇️
                        </button>
                        <button
                          onClick={() => movePanelOrder(panel.id, 'bottom')}
                          disabled={index === config.tab2.panels.length - 1}
                          className={styles.moveButton}
                          title="Move to bottom"
                        >
                          ⏬
                        </button>
                        <button
                          onClick={() => {
                            showConfirm({
                              title: 'Remove panel?',
                              message: `Remove "${panel.title}" and all its content?`,
                              confirmText: 'Remove',
                              cancelText: 'Cancel',
                              variant: 'danger',
                              onConfirm: () => handleConfigChange((prevConfig) => {
                                const filteredPanels = prevConfig.tab2.panels
                                  .filter((p: any) => p.id !== panel.id)
                                  .map((p: any, i: number) => ({ ...p, order: i + 1 }));
                                return {
                                  ...prevConfig,
                                  tab2: {
                                    ...prevConfig.tab2,
                                    panels: filteredPanels,
                                  },
                                };
                              }),
                            });
                          }}
                          className={styles.removeButton}
                          title="Remove panel"
                        >
                          🗑️
                        </button>
                        <button className={`${styles.expandIcon} ${expandedPanels.has(panel.id) ? styles.expanded : ''}`}>
                          ▼
                        </button>
                      </div>
                    </div>

                    {expandedPanels.has(panel.id) && (
                      <div className={styles.contentPanelBody}>
                        <PanelEditor
                          panels={[panel]}
                          programId={selectedProgram}
                          onChange={(updatedPanels) => {
                            const targetPanelId = panel.id;
                            handleConfigChange((prevConfig) => {
                              const updatedTab2Panels = prevConfig.tab2.panels.map((p: any) =>
                                p.id === targetPanelId ? updatedPanels[0] : p
                              );
                              return {
                                ...prevConfig,
                                tab2: {
                                  ...prevConfig.tab2,
                                  panels: updatedTab2Panels,
                                },
                              };
                            });
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}

                {config.tab2.panels.length === 0 && (
                  <div className={styles.emptyPanels}>
                    No content panels yet. Click "Add Panel" to create one.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Tab 3 Settings Panel */}
        <div className={styles.accordionPanel}>
          <div className={styles.accordionHeader} onClick={() => setExpandedTab3(!expandedTab3)}>
            <h3>Meetings Tab: {config.tab3?.name || 'Meetings & Workshops'}</h3>
            <button className={`${styles.expandIcon} ${expandedTab3 ? styles.expanded : ''}`}>
              ▼
            </button>
          </div>
          {expandedTab3 && (
            <div className={styles.accordionContent}>
              <div className={styles.formGroup}>
                <label>Tab Name</label>
                <input
                  type="text"
                  value={config.tab3?.name || 'Meetings & Workshops'}
                  onChange={(e) =>
                    handleConfigChange((prevConfig) => ({
                      ...prevConfig,
                      tab3: { ...(prevConfig.tab3 || {}), name: e.target.value },
                    }))
                  }
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Header Text</label>
                <textarea
                  value={config.tab3?.header_text || ''}
                  onChange={(e) =>
                    handleConfigChange((prevConfig) => ({
                      ...prevConfig,
                      tab3: { ...(prevConfig.tab3 || {}), header_text: e.target.value },
                    }))
                  }
                  className={styles.textarea}
                  rows={2}
                  placeholder="Optional header text to display above the content"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Confirmation Dialog */}
      {showSaveConfirmation && (
        <div className={styles.dialogOverlay} onClick={() => !saving && setShowSaveConfirmation(false)}>
          <div className={styles.dialogBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <h3>Confirm Publish</h3>
              <button onClick={() => setShowSaveConfirmation(false)} className={styles.dialogClose} disabled={saving}>
                ×
              </button>
            </div>
            <div className={styles.dialogContent}>
              <p><strong>You are about to publish these changes:</strong></p>
              <div className={styles.changeSummary}>{changeSummary}</div>
              <p>This will update the live site immediately.</p>
            </div>
            <div className={styles.dialogActions}>
              <button onClick={() => setShowSaveConfirmation(false)} className={styles.cancelButton} disabled={saving}>
                Cancel
              </button>
              <button onClick={handleSaveConfirm} className={styles.confirmButton} disabled={saving}>
                {saving ? 'Publishing...' : 'Publish Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Dialog */}
      {showHistory && (
        <div className={styles.dialogOverlay} onClick={() => setShowHistory(false)}>
          <div className={styles.dialogBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <h3>Change History</h3>
              <button onClick={() => setShowHistory(false)} className={styles.dialogClose}>
                ×
              </button>
            </div>
            <div className={styles.dialogContent}>
              {history.length === 0 ? (
                <p>No change history available</p>
              ) : (
                <div className={styles.historyList}>
                  {history.map((entry, index) => (
                    <div key={index} className={styles.historyEntry}>
                      <div className={styles.historyDate}>
                        {new Date(entry.timestamp).toLocaleString()}
                      </div>
                      <div className={styles.historyAdmin}>
                        By: {entry.admin_email}
                      </div>
                      <div className={styles.historySummary}>
                        {entry.change_summary}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function getAvailablePrograms(adminRights: string[]): string[] {
  if (!adminRights || adminRights.length === 0) return [];
  if (adminRights.includes('All')) return ['saa', 'aa', 'na'];
  
  const programs: string[] = [];
  if (adminRights.includes('SAA')) programs.push('saa');
  if (adminRights.includes('AA')) programs.push('aa');
  if (adminRights.includes('NA')) programs.push('na');
  
  return programs;
}

function generateChangeSummary(oldConfig: any, newConfig: any): string {
  const changes: string[] = [];

  // Check title/subtitle/logo changes
  if (oldConfig.title !== newConfig.title) {
    changes.push(`Changed title from '${oldConfig.title}' to '${newConfig.title}'`);
  }
  if (oldConfig.subtitle !== newConfig.subtitle) {
    changes.push('Updated subtitle');
  }
  if (oldConfig.logo_url !== newConfig.logo_url) {
    if (!oldConfig.logo_url && newConfig.logo_url) {
      changes.push('Added program logo');
    } else if (oldConfig.logo_url && !newConfig.logo_url) {
      changes.push('Removed program logo');
    } else {
      changes.push('Updated program logo');
    }
  }

  // Check Tab 1 changes
  const oldTab1 = oldConfig.tab1 || {};
  const newTab1 = newConfig.tab1 || {};
  if (oldTab1.name !== newTab1.name) {
    changes.push(`Changed Tab 1 name from '${oldTab1.name || 'Take Action'}' to '${newTab1.name || 'Take Action'}'`);
  }
  if (oldTab1.icon_url !== newTab1.icon_url) {
    if (newTab1.icon_url) {
      changes.push('Updated Tab 1 icon');
    } else {
      changes.push('Removed Tab 1 icon');
    }
  }
  if (oldTab1.accent_color !== newTab1.accent_color) {
    changes.push('Changed Tab 1 accent color');
  }

  // Check Tab 3 changes
  const oldTab3 = oldConfig.tab3 || {};
  const newTab3 = newConfig.tab3 || {};
  if (oldTab3.name !== newTab3.name) {
    changes.push(`Changed Meetings Tab name from '${oldTab3.name || 'Meetings & Workshops'}' to '${newTab3.name || 'Meetings & Workshops'}'`);
  }
  if (oldTab3.header_text !== newTab3.header_text) {
    changes.push('Updated Meetings Tab header text');
  }
  if (oldTab3.icon_url !== newTab3.icon_url) {
    if (newTab3.icon_url) {
      changes.push('Updated Tab 3 icon');
    } else {
      changes.push('Removed Tab 3 icon');
    }
  }
  if (oldTab3.accent_color !== newTab3.accent_color) {
    changes.push('Changed Tab 3 accent color');
  }

  // Check Tab 2 changes
  const oldTab2 = oldConfig.tab2 || {};
  const newTab2 = newConfig.tab2 || {};
  if (oldTab2.name !== newTab2.name) {
    changes.push(`Changed Resource Tab name from '${oldTab2.name}' to '${newTab2.name}'`);
  }
  if (oldTab2.header_text !== newTab2.header_text) {
    changes.push('Updated Resource Tab header text');
  }
  if (oldTab2.icon_url !== newTab2.icon_url) {
    if (newTab2.icon_url) {
      changes.push('Updated Tab 2 icon');
    } else {
      changes.push('Removed Tab 2 icon');
    }
  }
  if (oldTab2.accent_color !== newTab2.accent_color) {
    changes.push('Changed Tab 2 accent color');
  }

  // Check Tab 2 panels (Resource Tab)
  const oldPanels = (oldTab2.panels || []).reduce((acc: any, p: any, i: number) => {
    acc[p.id || `panel-${i}`] = p;
    return acc;
  }, {});
  const newPanels = (newTab2.panels || []).reduce((acc: any, p: any, i: number) => {
    acc[p.id || `panel-${i}`] = p;
    return acc;
  }, {});

  // Added panels
  Object.keys(newPanels).forEach(panelId => {
    if (!oldPanels[panelId]) {
      const panelTitle = newPanels[panelId].title || 'Untitled';
      changes.push(`Added new panel to Resource Tab: '${panelTitle}'`);
    }
  });

  // Removed panels
  Object.keys(oldPanels).forEach(panelId => {
    if (!newPanels[panelId]) {
      const panelTitle = oldPanels[panelId].title || 'Untitled';
      changes.push(`Removed panel from Resource Tab: '${panelTitle}'`);
    }
  });

  // Modified panels
  Object.keys(oldPanels).forEach(panelId => {
    if (newPanels[panelId]) {
      const oldPanel = oldPanels[panelId];
      const newPanel = newPanels[panelId];
      const panelTitle = newPanel.title || 'Untitled';

      // Check panel metadata changes
      if (oldPanel.title !== newPanel.title) {
        changes.push(`Edited panel title from '${oldPanel.title}' to '${newPanel.title}'`);
      }
      if (oldPanel.subtext !== newPanel.subtext) {
        changes.push(`Updated subtext for '${panelTitle}' panel`);
      }
      if (oldPanel.format !== newPanel.format) {
        changes.push(`Changed format for '${panelTitle}' panel from ${oldPanel.format} to ${newPanel.format}`);
      }
      if (oldPanel.order !== newPanel.order) {
        changes.push(`Reordered '${panelTitle}' panel`);
      }
      if (oldPanel.icon_url !== newPanel.icon_url) {
        if (newPanel.icon_url) {
          changes.push(`Updated icon for '${panelTitle}' panel`);
        } else {
          changes.push(`Removed icon from '${panelTitle}' panel`);
        }
      }
      if (oldPanel.accent_color !== newPanel.accent_color) {
        changes.push(`Changed accent color for '${panelTitle}' panel`);
      }
      if (oldPanel.show_item_count !== newPanel.show_item_count) {
        changes.push(`Updated item count display setting for '${panelTitle}' panel`);
      }

      // Check items within panel
      const oldItems = oldPanel.items || [];
      const newItems = newPanel.items || [];

      // Match items by position (index) for comparison
      const maxItems = Math.max(oldItems.length, newItems.length);
      for (let i = 0; i < maxItems; i++) {
        const oldItem = i < oldItems.length ? oldItems[i] : null;
        const newItem = i < newItems.length ? newItems[i] : null;

        if (!oldItem && newItem) {
          // Item added
          const itemTitle = (newItem.title || '').trim();
          if (itemTitle) {
            changes.push(`Added item '${itemTitle}' to '${panelTitle}' panel`);
          } else {
            const subtextPreview = ((newItem.subtext || '').trim()).substring(0, 50);
            if (subtextPreview) {
              changes.push(`Added item to '${panelTitle}' panel: '${subtextPreview}...'`);
            } else {
              changes.push(`Added item to '${panelTitle}' panel`);
            }
          }
        } else if (oldItem && !newItem) {
          // Item removed
          const itemTitle = (oldItem.title || '').trim();
          if (itemTitle) {
            changes.push(`Removed item '${itemTitle}' from '${panelTitle}' panel`);
          } else {
            const subtextPreview = ((oldItem.subtext || '').trim()).substring(0, 50);
            if (subtextPreview) {
              changes.push(`Removed item from '${panelTitle}' panel: '${subtextPreview}...'`);
            } else {
              changes.push(`Removed item from '${panelTitle}' panel`);
            }
          }
        } else if (oldItem && newItem) {
          // Item modified - check for specific field changes
          const itemTitle = ((newItem.title || '').trim()) || ((oldItem.title || '').trim());
          const itemIdentifier = itemTitle ? `'${itemTitle}'` : `item #${i + 1}`;

          if (oldItem.title !== newItem.title) {
            const oldTitle = (oldItem.title || '').trim() || '(empty)';
            const newTitle = (newItem.title || '').trim() || '(empty)';
            changes.push(`Updated title for ${itemIdentifier} in '${panelTitle}' panel: '${oldTitle}' → '${newTitle}'`);
          }

          if (oldItem.subtext !== newItem.subtext) {
            changes.push(`Updated subtext for ${itemIdentifier} in '${panelTitle}' panel`);
          }

          // Check image URL changes (key feature user requested)
          const oldImage = oldItem.image_url ? (oldItem.image_url || '').trim() : '';
          const newImage = newItem.image_url ? (newItem.image_url || '').trim() : '';
          if (oldImage !== newImage) {
            if (!oldImage && newImage) {
              changes.push(`Added image for ${itemIdentifier} in '${panelTitle}' panel`);
            } else if (oldImage && !newImage) {
              changes.push(`Removed image for ${itemIdentifier} in '${panelTitle}' panel`);
            } else {
              changes.push(`Updated image for ${itemIdentifier} in '${panelTitle}' panel`);
            }
          }

          // Check link changes
          if (oldItem.link_type !== newItem.link_type || oldItem.link_value !== newItem.link_value) {
            changes.push(`Updated link for ${itemIdentifier} in '${panelTitle}' panel`);
          }

          // Check text field (for quotes/other text items)
          if (oldItem.text !== newItem.text) {
            const textPreview = ((newItem.text || '').trim()).substring(0, 50);
            changes.push(`Updated text for ${itemIdentifier} in '${panelTitle}' panel: '${textPreview}...'`);
          }
        }
      }
    }
  });

  return changes.length > 0 ? changes.join(', ') : 'Minor updates';
}

export default ProgramsAdmin;

